import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

type SeoProps = {
  titleKey?: string;       // e.g. "home.title" in 'meta' ns
  descKey?: string;        // e.g. "home.desc"  in 'meta' ns
  title?: string;          // optional direct string
  description?: string;    // optional direct string
  imagePath?: string;      // "/og-image.jpg"
};

function getOrigin() {
  if (typeof window === "undefined") return "";
  return window.location.origin;
}

function splitLang(pathname: string) {
  const parts = pathname.split("/");
  const seg = (parts[1] || "").toLowerCase();
  const isLang = seg === "en" || seg === "ua";
  const rest = "/" + (isLang ? parts.slice(2).join("/") : parts.slice(1).join("/"));
  return { seg: isLang ? seg : "", rest: rest === "//" ? "/" : rest };
}

function setNamedMeta(name: string, content: string) {
  if (!content) return;
  let el = document.querySelector<HTMLMetaElement>(`meta[name="${name}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("name", name);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setPropMeta(prop: string, content: string) {
  if (!content) return;
  let el = document.querySelector<HTMLMetaElement>(`meta[property="${prop}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute("property", prop);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

export default function Seo({ titleKey, descKey, title, description, imagePath = "/og-image.jpg" }: SeoProps) {
  const { pathname, search } = useLocation();
  const { t, i18n } = useTranslation("meta");

  // force re-render on language changes
  const [, setTick] = useState(0);
  useEffect(() => {
    const onLang = () => setTick(v => v + 1);
    i18n.on("languageChanged", onLang);
    return () => i18n.off("languageChanged", onLang);
  }, [i18n]);

  const origin = getOrigin();
  const { seg, rest } = splitLang(pathname);

  // compute translated strings every render (cheap and safe)
  const finalTitle = titleKey ? t(titleKey) : (title ?? "");
  const finalDesc  = descKey  ? t(descKey)  : (description ?? "");

  // set browser tab + key metas immediately
  useEffect(() => {
    if (finalTitle) document.title = finalTitle;
    if (finalDesc) {
      setNamedMeta("description", finalDesc);
      setPropMeta("og:description", finalDesc);
      setNamedMeta("twitter:description", finalDesc);
    }
    if (finalTitle) {
      setPropMeta("og:title", finalTitle);
      setNamedMeta("twitter:title", finalTitle);
    }
  }, [finalTitle, finalDesc]);

  const canonical = origin + pathname + search;
  const enUrl = origin + "/en" + (rest === "/" ? "/" : rest) + search;
  const uaUrl = origin + "/ua" + (rest === "/" ? "/" : rest) + search;
  const ogLocale = (seg === "ua" || i18n.language === "uk") ? "uk_UA" : "en_US";
  const imageAbs = imagePath.startsWith("http") ? imagePath : origin + imagePath;

  // re-mount Helmet when lang or URL changes
  const helmetKey = `${i18n.resolvedLanguage || i18n.language}|${pathname}${search}`;

  return (
    <Helmet key={helmetKey} htmlAttributes={{ lang: ogLocale === "uk_UA" ? "uk" : "en" }}>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />

      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={enUrl} />
      <link rel="alternate" hrefLang="uk" href={uaUrl} />
      <link rel="alternate" hrefLang="x-default" href={origin + "/"} />

      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonical} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={imageAbs} />
      <meta property="og:locale" content={ogLocale} />
      <meta property="og:locale:alternate" content={ogLocale === "uk_UA" ? "en_US" : "uk_UA"} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={imageAbs} />
    </Helmet>
  );
}
